import { ingestMediaFromUrl, isStoredMediaUrl, uploadMediaFile } from '../services/mediaService';
import type { Recipe } from '../types';

export interface RecipeImageFields {
    readonly imageUrl?: string;
    readonly imageUrls?: string[];
    readonly imageMediaIds?: string[];
}

export function resolveRecipeImageSrc(url?: string): string | undefined {
    const trimmed = url?.trim();
    if (!trimmed) return undefined;
    if (isStoredMediaUrl(trimmed)) return trimmed;
    return trimmed;
}

export function getRecipeHeaderImageUrl(
    recipe: Pick<Recipe, 'imageUrl' | 'imageUrls'>,
): string | undefined {
    return resolveRecipeImageSrc(recipe.imageUrls?.[0] || recipe.imageUrl);
}

export function getRecipeGalleryImageUrls(
    recipe: Pick<Recipe, 'imageUrl' | 'imageUrls'>,
): string[] {
    const raw = recipe.imageUrls?.length
        ? recipe.imageUrls
        : recipe.imageUrl
            ? [recipe.imageUrl]
            : [];
    const seen = new Set<string>();
    const result: string[] = [];
    for (const url of raw) {
        const resolved = resolveRecipeImageSrc(url);
        if (resolved && !seen.has(resolved)) {
            seen.add(resolved);
            result.push(resolved);
        }
    }
    return result;
}

export function getRecipeImageFieldsGalleryUrls(fields?: RecipeImageFields | null): string[] {
    if (!fields) return [];
    return getRecipeGalleryImageUrls(fields);
}

export function buildAppendedRecipeImageFields(
    readUrl: string,
    imageId: string | undefined,
    existing?: RecipeImageFields,
): RecipeImageFields {
    const existingUrls = getRecipeImageFieldsGalleryUrls(existing);
    if (existingUrls.includes(readUrl)) {
        return {
            imageUrl: existing?.imageUrl || readUrl,
            imageUrls: existingUrls,
            imageMediaIds: existing?.imageMediaIds || [],
        };
    }

    const imageUrls = [...existingUrls, readUrl];
    const imageMediaIds = imageId
        ? [...(existing?.imageMediaIds || []), imageId]
        : existing?.imageMediaIds || [];

    return {
        imageUrl: existing?.imageUrl || readUrl,
        imageUrls,
        imageMediaIds,
    };
}

export function removeRecipeGalleryImageAt(
    existing: RecipeImageFields,
    index: number,
): RecipeImageFields {
    const urls = getRecipeImageFieldsGalleryUrls(existing);
    if (index < 0 || index >= urls.length) {
        return existing;
    }

    const nextUrls = urls.filter((_, idx) => idx !== index);
    const mediaIds = [...(existing.imageMediaIds || [])];
    const nextMediaIds = mediaIds.length === urls.length
        ? mediaIds.filter((_, idx) => idx !== index)
        : mediaIds.slice(0, nextUrls.length);

    if (nextUrls.length === 0) {
        return { imageUrls: [], imageMediaIds: [] };
    }

    return {
        imageUrl: nextUrls[0],
        imageUrls: nextUrls,
        imageMediaIds: nextMediaIds,
    };
}

export function buildPrimaryRecipeImageFields(
    readUrl: string,
    imageId: string | undefined,
    existing?: RecipeImageFields,
): RecipeImageFields {
    const imageUrls = [
        readUrl,
        ...(existing?.imageUrls || []).filter((url) => url !== readUrl),
    ];
    const imageMediaIds = imageId
        ? [imageId, ...(existing?.imageMediaIds || [])]
        : existing?.imageMediaIds || [];

    return {
        imageUrl: readUrl,
        imageUrls,
        imageMediaIds,
    };
}

export async function appendRecipeGalleryImage(
    recipeId: string,
    file: File,
    existing?: RecipeImageFields,
): Promise<RecipeImageFields> {
    const uploaded = await uploadMediaFile('recipe_image', file, { recipeId });
    return buildAppendedRecipeImageFields(uploaded.readUrl, uploaded.imageId, existing);
}

export async function appendRecipeGalleryImageFromUrl(
    recipeId: string,
    sourceUrl: string,
    existing?: RecipeImageFields,
): Promise<RecipeImageFields> {
    const trimmed = sourceUrl.trim();
    if (!trimmed) {
        return {
            imageUrls: getRecipeImageFieldsGalleryUrls(existing),
            imageMediaIds: existing?.imageMediaIds || [],
            imageUrl: existing?.imageUrl,
        };
    }

    if (isStoredMediaUrl(trimmed)) {
        return buildAppendedRecipeImageFields(trimmed, undefined, existing);
    }

    const ingested = await ingestMediaFromUrl('recipe_image', trimmed, { recipeId });
    return buildAppendedRecipeImageFields(ingested.readUrl, ingested.imageId, existing);
}

export async function uploadRecipeHeaderImage(
    recipeId: string,
    file: File,
    existing?: RecipeImageFields,
): Promise<RecipeImageFields> {
    const uploaded = await uploadMediaFile('recipe_image', file, { recipeId });
    return buildPrimaryRecipeImageFields(uploaded.readUrl, uploaded.imageId, existing);
}

export async function uploadRecipeHeaderImageFromUrl(
    recipeId: string,
    sourceUrl: string,
    existing?: RecipeImageFields,
): Promise<RecipeImageFields> {
    const ingested = await ingestMediaFromUrl('recipe_image', sourceUrl, { recipeId });
    return buildPrimaryRecipeImageFields(ingested.readUrl, ingested.imageId, existing);
}

export async function appendRecipeImageFromUrl(
    recipeId: string,
    sourceUrl: string,
    existing?: RecipeImageFields,
): Promise<RecipeImageFields> {
    const trimmed = sourceUrl.trim();
    if (!trimmed) {
        return {
            imageUrls: existing?.imageUrls || [],
            imageMediaIds: existing?.imageMediaIds || [],
            imageUrl: existing?.imageUrl,
        };
    }

    if (isStoredMediaUrl(trimmed)) {
        const imageUrls = [...(existing?.imageUrls || [])];
        if (!imageUrls.includes(trimmed)) {
            imageUrls.push(trimmed);
        }
        return {
            imageUrls,
            imageMediaIds: existing?.imageMediaIds || [],
            imageUrl: existing?.imageUrl || trimmed,
        };
    }

    const ingested = await ingestMediaFromUrl('recipe_image', trimmed, { recipeId });
    return buildPrimaryRecipeImageFields(ingested.readUrl, ingested.imageId, existing);
}
